import React, { useEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash';

import textUtils from '@powerednow/shared/modules/utilities/textUtils';
import Box from '@powerednow/components/Box/Box';
import SearchField from '@powerednow/components/AdvancedSearch/SearchField';
import AppliedFiltersBar from '@powerednow/components/AdvancedSearch/AppliedFiltersBar';
import SearchResultsCategoriesBar from '@powerednow/components/AdvancedSearch/ResultCategoriesBar';
import { CreateExtComponent } from '@powerednow/client-shared/Components/react/React';
import { DataWrapper } from '@powerednow/app/FieldServices/singletons/DataWrapper';

declare let handleClientError: any;

function FullTextSearchField(props: {
    storeName: string,
    searchResultHandler: (matchIds: number[], searchTerms?: string[]) => void,
    loadMaskToggleFn: (showMask: boolean) => void,
    placeholderText?: string,
    externalFilterProps?: string[] | 'fullRecord',
    externalFilters?: Array<{key: string, value: any, filterFn?: ((record: any) => boolean), static?: boolean} >,
    resultKeyMap?: {[key: string]: {groupName: string, priority: number}},
    isRendered?: boolean,
    autoFocus?: boolean,
    searchOnly?: string[],
}) {
    const {
        storeName,
        searchResultHandler,
        loadMaskToggleFn,
        placeholderText,
        resultKeyMap,
        externalFilterProps,
        externalFilters,
        isRendered,
        autoFocus,
        searchOnly,
    } = props;

    const [propSearchCats, setPropSearchCats] = useState([]);
    const [appliedMatchesProp, setAppliedMatchesProp] = useState([]);
    const [searchInProgress, setSearchInProgress] = useState(false);
    const [disableInput, setDisableInput] = useState(false);
    const searchProgressStatus = useRef<'complete'|'typing'|'searching'>('complete');
    const extraFilters = useRef(externalFilters);
    const searchOnlyFields = useRef(searchOnly);
    const rendered = useRef(isRendered);
    const searchText = useRef('');
    const currentSearchResults = useRef(null);
    const allowNonPrefixSearch = useRef<boolean>(false);
    const currentSearchResultCategories = useRef<{label: string, values: any[], priority: number}[]>([]);
    const appliedMatches = useRef<{label: string, matchRecords: any[], searchTerm: string}[]>([]);
    const searchIterator = useRef<AsyncGenerator>(null);
    const inputCmp = useRef<{clearInputWithoutSearch:() => void, focusInput:() => void}>();
    const lastSearchTrigger = useRef<number>(0);

    const applyExternalFiltersToRecords = (records: any[], staticOnly: boolean = false) => (records || []).filter((record: any) => {
        if (!extraFilters.current) {
            return true;
        }
        let matchesFilters = true;
        extraFilters.current.forEach(filter => {
            if (staticOnly === Boolean(filter.static)) {
                if (filter.filterFn) {
                    matchesFilters = matchesFilters && filter.filterFn(record);
                } else {
                    matchesFilters = matchesFilters && record[filter.key] === filter.value;
                }
            }
        });
        return matchesFilters;
    });

    const updateSelectableSearchCategories = () => {
        const unfilteredResultCategories = currentSearchResultCategories.current;
        const filteredCategories = unfilteredResultCategories.map(category => ({
            label: category.label,
            priority: category.priority,
            values: applyExternalFiltersToRecords(category.values),
        }));
        setPropSearchCats(filteredCategories.filter(category => category.values.length > 0));
    };

    const getAppliedSearchTerms = () => {
        const searchTerms = [];
        if (searchText.current !== '') {
            searchTerms.push(...searchText.current.split(' '));
        }
        if (appliedMatches.current.length) {
            appliedMatches.current.forEach(appliedMatch => {
                searchTerms.push(...appliedMatch.searchTerm.split(' '));
            });
        }
        return searchTerms;
    };

    const getAppliedMatches = () => {
        const allAppliedMatches = [...appliedMatches.current];
        if ((searchText.current !== '' || allAppliedMatches.length > 0) && currentSearchResults.current) {
            allAppliedMatches.unshift({ label: '', matchRecords: currentSearchResults.current, searchTerm: searchText.current });
        }
        if (allAppliedMatches.length) {
            return allAppliedMatches.reduce((matchedRecords, appliedMatch, index) => {
                const filteredRecords = applyExternalFiltersToRecords(appliedMatch.matchRecords);
                if (index === 0) {
                    matchedRecords.push(...filteredRecords);
                    return matchedRecords;
                }
                return matchedRecords.filter((record: any) => filteredRecords.map((filteredRecord: any) => filteredRecord.id).includes(record.id));
            }, []);
        }
        return null;
    };
    
    const getAppliedMatchIds = () => {
        const appliedMatchRecords = getAppliedMatches();
        if (appliedMatchRecords) {
            return appliedMatchRecords.map((record: any) => record.id);
        }
        return null;
    };

    const applyFilter = (idArray: number[], isRefinement = false) => {
        const lastAppliedMatches = getAppliedMatchIds();
        const filteredIds = lastAppliedMatches ? lastAppliedMatches.filter((id: number) => idArray.includes(id)) : idArray;
        if (isRefinement) {
            setDisableInput(filteredIds.length < 10);
        }
        searchResultHandler(filteredIds, getAppliedSearchTerms());
    };

    const processMatches = (matches: {record: {id: number} & any, matchedFields: string[]}[]) => {
        const results = currentSearchResults.current || [];
        const resultCategories = currentSearchResultCategories.current;
        matches.forEach(match => {
            if (applyExternalFiltersToRecords([match.record], true).length !== 1) {
                return;
            }
            if (!results.some(resultRec => resultRec.id === match.record.id)) {
                results.push(match.record);
                currentSearchResults.current = results;
            }
            match.matchedFields.forEach(matchKey => {
                let keyMatched = false;
                if (resultKeyMap) {
                    let resultGroupConfig = resultKeyMap[matchKey];
                    if (!resultGroupConfig) {
                        const wildCardMatchKey = Object.keys(resultKeyMap).filter(resultKey => resultKey.includes('.*')).find(matcher => matchKey.match(matcher));
                        if (wildCardMatchKey) {
                            resultGroupConfig = resultKeyMap[wildCardMatchKey];
                        }
                    }
                    if (resultGroupConfig) {
                        keyMatched = true;
                        const { groupName: matchGroupName, priority: matchPriority } = resultGroupConfig;
                        const groupIndex = resultCategories.findIndex(resultGroup => resultGroup.label === matchGroupName);
                        if (groupIndex > -1) {
                            /**
                             * @TODO Review this.
                             * We end up here with the same records already included, but actually in a different
                             * object instance. Lets match for id, but I have a strong feeling we send duplicated results
                             * here accidently.
                             */
                            if (!resultCategories[groupIndex].values.some((value => value.id === match.record.id))) {
                                resultCategories[groupIndex].values.push(match.record);
                            }
                        } else {
                            resultCategories.push({
                                values: [match.record],
                                priority: matchPriority,
                                label: matchGroupName,
                            });
                        }
                    }
                }
                if (!keyMatched) {
                    console.warn(`UNHANDLED SEARCH RESULT KEY ${matchKey}`);
                }
            });
        });

        currentSearchResultCategories.current = resultCategories;

        updateSelectableSearchCategories();
    };

    const abortCurrentSearch = async (abortCalled = false) => new Promise<void>((resolve, reject) => {
        if (searchIterator.current === null) {
            resolve();
        } else {
            if (!abortCalled) {
                searchIterator.current.return({ done: true });
            }
            setTimeout(() => abortCurrentSearch(true), 100);
        }
    });

    const runSearch = async () => {
        if (searchText.current !== '') {
            loadMaskToggleFn(true);
            await abortCurrentSearch();
            setSearchInProgress(true);
            currentSearchResults.current = null;
            currentSearchResultCategories.current = [];
            setPropSearchCats([]);
            const matchOnly = textUtils.isUKPostcode(searchText.current) ? 'postcode' : undefined;
            const searchTerm = allowNonPrefixSearch.current ? `~${searchText.current}` : searchText.current;
            const { iterator } = await DataWrapper.performFullTextSearch(storeName, searchTerm, externalFilterProps, searchOnlyFields.current, matchOnly);
            searchIterator.current = iterator.searchIterator;
            let nextResult = await searchIterator.current.next();
            if (nextResult.done) {
                currentSearchResults.current = [];
                searchResultHandler([]);
            } else {
                while (!nextResult.done && searchProgressStatus.current !== 'typing') {
                    processMatches(nextResult.value);
                    // eslint-disable-next-line no-await-in-loop
                    nextResult = await searchIterator.current.next();
                }
                if (searchProgressStatus.current !== 'typing') {
                    const filteredResultIds = applyExternalFiltersToRecords(currentSearchResults.current).map(rec => rec.id);
                    applyFilter(filteredResultIds);
                }
            }
            searchProgressStatus.current = 'complete';
            setSearchInProgress(false);
            loadMaskToggleFn(false);
            searchIterator.current = null;
        }
    };

    const checkTypingDelayAndSearch = () => {
        setTimeout(() => {
            const currentTime = new Date().getTime();
            if (currentTime - lastSearchTrigger.current > 550) {
                searchProgressStatus.current = 'searching';
                runSearch().catch(handleClientError);
            } else {
                checkTypingDelayAndSearch();
            }
        }, 500);
    };

    const onPrefixSearchSettingChanged = (value: boolean) => {
        allowNonPrefixSearch.current = value;
        runSearch().catch(handleClientError);
    };

    const onSearchFieldValueChange = async (value: string) => {
        const valueChanged = searchText.current !== value;
        if (valueChanged) {
            searchText.current = value;
            if (value === '') {
                searchProgressStatus.current = 'complete';
                setSearchInProgress(false);
                currentSearchResults.current = null;
                currentSearchResultCategories.current = [];
                setPropSearchCats([]);
                searchResultHandler(getAppliedMatchIds(), getAppliedSearchTerms());
            } else {
                lastSearchTrigger.current = new Date().getTime();
                if (searchProgressStatus.current !== 'typing') {
                    searchProgressStatus.current = 'typing';
                    checkTypingDelayAndSearch();
                }
            }
        }
    };

    const applyCategoryFilter = (selection: {label: string, values: any[]}) => {
        const { label, values } = selection;
        appliedMatches.current.push({ label: `${searchText.current} in ${label}`, matchRecords: values, searchTerm: searchText.current });
        applyFilter(values.map(value => (value.get ? value.get('id') : value.id)), true);
        setAppliedMatchesProp(appliedMatches.current);
        currentSearchResultCategories.current = [];
        updateSelectableSearchCategories();

        if (inputCmp.current) {
            inputCmp.current.clearInputWithoutSearch();
            searchText.current = '';
        }
    };

    const clearLastAppliedMatch = async () => {
        appliedMatches.current = appliedMatches.current.slice(0, -1);
        setAppliedMatchesProp(appliedMatches.current);
        const matchIds = getAppliedMatchIds();
        if (matchIds === null || matchIds.length > 10) {
            setDisableInput(false);
            if (inputCmp.current) {
                inputCmp.current.focusInput();
            }
        }
        searchResultHandler(getAppliedMatchIds(), getAppliedSearchTerms());
    };

    useEffect(() => {
        const filtersChanged = !isEqual(externalFilters, extraFilters.current);
        if (filtersChanged) {
            extraFilters.current = externalFilters;
            updateSelectableSearchCategories();
            searchResultHandler(getAppliedMatchIds(), getAppliedSearchTerms());
        }
        const searchOnlyChanged = !isEqual(searchOnly, searchOnlyFields.current);
        if (searchOnlyChanged) {
            searchOnlyFields.current = searchOnly;
            runSearch().catch(handleClientError);
        }
        const isShown = rendered.current !== isRendered && isRendered === true;
        if (isShown && autoFocus) {
            inputCmp.current.focusInput();
        }
    }, [externalFilters, isRendered, searchOnly]);

    return (
        <Box>
            <SearchField
                ref={inputCmp}
                disabled={disableInput}
                nonPrefixMatching={allowNonPrefixSearch.current}
                onPrefixSettingChange={onPrefixSearchSettingChanged}
                onValueChange={onSearchFieldValueChange}
                placeholderText={placeholderText}
            />
            <AppliedFiltersBar appliedFilters={appliedMatchesProp} onClearButtonTap={clearLastAppliedMatch} clearDisabled={searchInProgress} />
            <SearchResultsCategoriesBar resultCategories={propSearchCats} onAddButtonTap={applyCategoryFilter} searchInProgress={searchInProgress} />
        </Box>
    );
}

export default CreateExtComponent('FieldServices.widgets.FullTextSearchField', FullTextSearchField);
